// src/services/auth/authService.ts
import { 
  signInWithEmailAndPassword, 
  User,
  AuthError
} from 'firebase/auth';
import { auth } from '../../config/firebase';
import { useAuthStore } from '../../store/authStore';
import { persistUserData } from './userPersistence';
import { setPersistenceMode } from './persistenceService';
import { syncPointsAfterPurchase } from '../points/pointsService';
import { isUserBlocked, recordFailedLoginAttempt, resetLoginAttempts } from './loginAttemptsService';

export class LoginError extends Error {
  code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'LoginError';
    this.code = code;
  }
}

const isFirebaseAuthError = (error: unknown): error is AuthError =>
  typeof error === 'object' &&
  error !== null &&
  'code' in error &&
  typeof (error as { code: unknown }).code === 'string' &&
  String((error as { code: string }).code).startsWith('auth/');

export const signIn = async (
  email: string,
  password: string,
  rememberMe: boolean = false
): Promise<User> => {
  const normalizedEmail = String(email || '').trim().toLowerCase();

  if (!auth) {
    throw new LoginError('auth/not-initialized', 'Service d’authentification indisponible.');
  }

  // Vérifier si l'utilisateur est bloqué (best-effort si rules bloquent la lecture)
  const blocked = await isUserBlocked(normalizedEmail);
  if (blocked) {
    throw new LoginError(
      'auth/too-many-attempts',
      'Trop de tentatives de connexion. Votre compte est temporairement bloqué. Veuillez réessayer plus tard.'
    );
  }

  await setPersistenceMode(rememberMe);

  let user: User;
  try {
    const result = await signInWithEmailAndPassword(auth, normalizedEmail, password);
    user = result.user;
  } catch (error) {
    // Uniquement les vrais échecs d'identifiants Firebase comptent
    await recordFailedLoginAttempt(normalizedEmail);

    if (isFirebaseAuthError(error)) {
      if (error.code === 'auth/too-many-requests') {
        throw new LoginError(
          error.code,
          'Trop de tentatives. Réessayez dans quelques minutes ou réinitialisez le mot de passe.'
        );
      }
      if (
        error.code === 'auth/invalid-credential' ||
        error.code === 'auth/wrong-password' ||
        error.code === 'auth/user-not-found' ||
        error.code === 'auth/invalid-email'
      ) {
        throw new LoginError(error.code, 'Email ou mot de passe invalide');
      }
      throw new LoginError(error.code, error.message || 'Erreur d’authentification');
    }

    throw error;
  }

  // À partir d'ici, le mot de passe est correct : ne plus traiter les erreurs comme "invalid credentials"
  try {
    await resetLoginAttempts(normalizedEmail);
  } catch (error) {
    console.warn('Unable to reset login attempts:', error);
  }

  try {
    await syncPointsAfterPurchase(user.uid, normalizedEmail);
  } catch (error) {
    console.warn('Points sync failed after login (non-blocking):', error);
  }

  if (rememberMe) {
    try {
      await persistUserData(user);
    } catch (error) {
      console.warn('Unable to persist user data:', error);
    }
  }

  const { setUser } = useAuthStore.getState();
  await setUser(user);

  return user;
};

export const signOut = async (): Promise<void> => {
  try {
    // Vider le store avant Firebase pour éviter une redirection login → app
    // basée sur un état admin encore persisté.
    useAuthStore.getState().clearState();

    if (auth) {
      await auth.signOut();
    }

    // Clear all storage
    localStorage.clear();
    sessionStorage.clear();

    // Clear cookies
    document.cookie.split(';').forEach(cookie => {
      document.cookie = cookie
        .replace(/^ +/, '')
        .replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
    });
  } catch (error) {
    console.error('Logout error:', error);
    throw error;
  }
};
